import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DagAnalyticsService } from 'src/app/service/dag-analytics.service';
import { DagAnalyticsModel } from 'src/app/models/dag-analytics/dag-analytics.model';
import { NotificationService } from 'src/app/service/notification.service';
import { NotificationType } from 'src/app/enum/notification-type.enum';
import { GlobalService } from 'src/app/service/global.service';
import { AuthGaurdService } from 'src/app/service/auth-gaurd.service';
import { TableColumn } from 'src/app/home/table/TableColumn';
import { ExportExcelService } from 'src/app/service/export-excel.service';
 
@Component({
  selector: 'app-dag-data-analytics',
  templateUrl: './dag-data-analytics.component.html',
  styleUrls: ['./dag-data-analytics.component.css'],
  encapsulation: ViewEncapsulation.Emulated
})
export class DagDataAnalyticsComponent implements OnInit, OnDestroy {
  // Custom table properties - following template-view pattern
  dagAnalyticsData: any[] = [];
  excelDagAnalyticsData: DagAnalyticsModel[] = [];
  ordersTableColumns1: TableColumn[] = [];
  columnsData: any;
  actions: any = [];
  actionButtons: any = [];
 
  // Standard component properties
  private subscriptions: Subscription[] = [];
  public refreshing: boolean = false;
  public showLoading: boolean = false;
  isAccessDisabled: boolean = false;
  loggedinUser: string = '';
 
  // Excel export properties
  columnsExcelData: any;
  dataForExcel = [];
  ordersTableExcelHeaderColumns: TableColumn[];
 
  @ViewChild('TABLE') table: ElementRef;
 
  // Days of week mapping for display
  daysOfWeek: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // New properties for card-based dashboard
  selectedFilter: string = 'deviation';
  //searchTerm: string = '';
  filteredData: any[] = [];
  selectedDagForDetails: any = null;
  selectedDagName: string ='';
  selectedDateFilter: string = '';
 
  constructor(
    private globalService: GlobalService,
    private router: Router,
    private httpService: HttpClient,
    private dagAnalyticsService: DagAnalyticsService,
    public ete: ExportExcelService,
    private authGaurdService: AuthGaurdService,
    private notificationService: NotificationService
  ) { }
 
  ngOnInit(): void {
    // Get Permissions
    this.isAccessDisabled = this.globalService._permissionCheck('dag-data-analytics');
   
    // Get logged in user
    this.loggedinUser = this.authGaurdService.loginUser();
   
    // Initialize table structure
    this.initializeColumns();
    this.initializeExcelHeaders();
   
    // Set default date filter to current date
    this.setDefaultDateFilter();
    
    // Load data
    this.getDagAnalyticsList();
  }
 
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
 
  /**
   * Load DAG analytics data from the backend - following template pattern
   */
  public getDagAnalyticsList(): void {
    this.refreshing = true;
    this.subscriptions.push(
      this.dagAnalyticsService.getDagAnalyticsLastWeek().subscribe(
        (response: DagAnalyticsModel[]) => {
          // Process data to add computed fields for table display
          this.dagAnalyticsData = response.map(item => ({
            ...item,
            dayOfWeekName: this.getDayOfWeekName(item.dayOfWeek),
            percentageDifference: this.getPercentageDifference(item.dayCount, item.avgDayCount),
            deviationStatus: this.isSignificantDifference(item.dayCount, item.avgDayCount) ? 'Deviation' : 'Normal',
            loadDate: this.parseLocalDate(item.loadDate)
          }));
         
          this.excelDagAnalyticsData = JSON.parse(JSON.stringify(this.dagAnalyticsData));
         
          // Sort by DAG name and then by date
          this.dagAnalyticsData = this.dagAnalyticsData.sort((a, b) => {
            if (a.dagName < b.dagName) return -1;
            if (a.dagName > b.dagName) return 1;
            return new Date(b.loadDate).getTime() - new Date(a.loadDate).getTime();
          });
         
          this.refreshing = false;
          
          // Apply default filters after data is loaded
          this.updateFilteredData();
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, 'Failed to load DAG analytics data');
          this.refreshing = false;
        }
      )
    );
  }
 
  /**
   * Initialize table columns - DAG Analytics specific columns
   */
  initializeColumns(): void {
    // Define columns for DAG Analytics table
    this.ordersTableColumns1 = [
      { name: 'DAG Name', dataKey: 'dagName', position: 'left', isSortable: true },
      { name: 'Load Date', dataKey: 'loadDate', position: 'left', isSortable: true },
      { name: 'Day of Week', dataKey: 'dayOfWeekName', position: 'left', isSortable: false },
      { name: 'Day Count', dataKey: 'dayCount', position: 'right', isSortable: true },
      { name: 'Avg Day Count', dataKey: 'avgDayCount', position: 'right', isSortable: true },
      { name: 'Difference (%)', dataKey: 'percentageDifference', position: 'right', isSortable: true },
      { name: 'Status', dataKey: 'deviationStatus', position: 'left', isSortable: false }
    ];
   
    // No row actions needed for this view-only table
    this.actions = [];
   
    // Add refresh button
    this.actionButtons = [
      {
        name: 'Refresh',
        class: 'btn btn-primary btn-xs'
      }
    ];
  }
 
  /**
   * Initialize Excel export headers
   */
  initializeExcelHeaders(): void {
    // Define excel export columns
    this.ordersTableExcelHeaderColumns = [
      { name: 'DAG Name', dataKey: 'dagName' },
      { name: 'Load Date', dataKey: 'loadDate' },
      { name: 'Day of Week', dataKey: 'dayOfWeekName' },
      { name: 'Day Count', dataKey: 'dayCount' },
      { name: 'Average Day Count', dataKey: 'avgDayCount' },
      { name: 'Percentage Difference', dataKey: 'percentageDifference' },
      { name: 'Status', dataKey: 'deviationStatus' }
    ];
  }
 
  /**
   * Handle row actions (none for this read-only table)
   */
  rowAction(row: any) {
    // No row actions needed for DAG Analytics view
  }
 
  /**
   * Handle action buttons
   */
  actionOnButton(action: any) {
    if (action === 'Refresh') {
      if (this.isAccessDisabled === true) {
        this.notificationService.sendNotification(NotificationType.WARNING, 'You do not have permissions to refresh this data.');
      } else {
        this.getDagAnalyticsList();
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Data refreshed successfully.');
      }
    }
  }
 
//   /**
//    * Export to Excel
//    */
//  exportExcel() {
//     // Prepare data for excel export with proper column mapping
//     const exportData = this.excelDagAnalyticsData.map(item => {
//       const processedItem = {
//         ...item,
//         dayOfWeekName: this.getDayOfWeekName(item.dayOfWeek),
//         percentageDifference: this.getPercentageDifference(item.dayCount, item.avgDayCount) + '%',
//         deviationStatus: this.isSignificantDifference(item.dayCount, item.avgDayCount) ? 'Deviation' : 'Normal',
//         loadDate: this.formatDate(new Date(item.loadDate))
//       };
//       return processedItem;
//     });
//     // Create data rows using the column order and mapping
//     this.dataForExcel = [];
//     exportData.forEach((row: any) => {
//       const rowData = this.ordersTableExcelHeaderColumns.map(col => row[col.dataKey]);
//       this.dataForExcel.push(rowData);
//     });
//     // Use the proper column names from ordersTableExcelHeaderColumns
//     const columnHeaders = this.ordersTableExcelHeaderColumns.map(col => col.name);
//     const reportData = {
//       title: 'DAG_Analytics_Report',
//       data: this.dataForExcel,
//       headers: columnHeaders
//     };
//     this.ete.exportExcel(reportData);
//   }
  /**
   * Get day of week name from number
   */
  getDayOfWeekName(dayOfWeek: number): string {
    if (dayOfWeek >= 0 && dayOfWeek < this.daysOfWeek.length) {
      return this.daysOfWeek[dayOfWeek];
    }
    return 'Unknown';
  }
 
  /**
   * Format date for display (timezone-safe)
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    
    let d: Date;
    if (typeof date === 'string') {
      // Parse as local date to avoid timezone issues
      const parts = date.split('-');
      if (parts.length === 3) {
        d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        d = new Date(date);
      }
    } else {
      d = new Date(date);
    }
    
    // Use timezone-safe formatting
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${year}`;
  }
 
  /**
   * Get percentage difference from average
   */
  getPercentageDifference(dayCount: number, avgDayCount: number): string {
    if (avgDayCount === 0) return '0';
    const diff = Math.abs(((dayCount - avgDayCount) / avgDayCount) * 100);
    return diff.toFixed(1);
  }
 
  /**
   * Check if the difference is significant (>15%)
   */
  isSignificantDifference(dayCount: number, avgDayCount: number): boolean {
    if (avgDayCount === 0) return false;
    const diff = Math.abs(((dayCount - avgDayCount) / avgDayCount) * 100);
    return diff > 15.0;
  }

  // ==== NEW DASHBOARD HELPER METHODS ====
  // These methods support the new card-based UI while keeping existing functionality intact

  /**
   * Get total number of unique DAGs
   */
  getTotalDAGs(): number {
    return this.dagAnalyticsData.length;
  }

  /**
   * Get number of DAGs with normal status
   */
  getNormalDAGs(): number {
    return this.dagAnalyticsData.filter(dag => dag.deviationStatus === 'Normal').length;
  }

  /**
   * Get number of DAGs with deviation status
   */
  getDeviationDAGs(): number {
    return this.dagAnalyticsData.filter(dag => dag.deviationStatus === 'Deviation').length;
  }

  /**
   * Get average deviation percentage across all DAGs
   */
  getAverageDeviation(): string {
    if (this.dagAnalyticsData.length === 0) return '0.0';
    
    const totalDeviation = this.dagAnalyticsData.reduce((sum, dag) => {
      return sum + parseFloat(dag.percentageDifference);
    }, 0);
    
    const avgDeviation = totalDeviation / this.dagAnalyticsData.length;
    return avgDeviation.toFixed(1);
  }

  /**
   * Set filter for DAGs
   */
  setFilter(filter: string): void {
    this.selectedFilter = filter;
    this.updateFilteredData();
  }

  /**
   * Handle search term changes
   */
  // onSearchChange(): void {
  //   this.updateFilteredData();
  // }

    /**
   * Clear all filters and search (legacy method - keeping for backward compatibility)
   */
  clearFilters(): void {
    this.clearAllFilters();
  }
 
  /**
   * Clear all filters, search, and advanced filters
   */
  clearAllFilters(): void {
    this.selectedFilter = 'all';
    this.selectedDagName = '';
    this.selectedDateFilter = 'all';
    //this.searchTerm = '';
    this.updateFilteredData();
  }
  
  /**
   * Set default date filter to yesterday's date
   */
  private setDefaultDateFilter(): void {
    const now = new Date();
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const yesterdayString = yesterday.toISOString().split('T')[0]; // YYYY-MM-DD format
    this.selectedDateFilter = yesterdayString;
  }
  
  /**
   * Parse date string as local date to avoid timezone issues
   */
  private parseLocalDate(dateString: string | Date): Date {
    if (!dateString) return new Date();
    
    if (dateString instanceof Date) {
      return dateString;
    }
    
    // Convert to string if it's not already
    const dateStr = String(dateString);
    
    // Backend sends LocalDate as YYYY-MM-DD string, parse as local date
    if (dateStr.includes('-')) {
      const datePart = dateStr.split('T')[0]; // Get date part only
      const parts = datePart.split('-');
      
      if (parts.length === 3) {
        // Create local date (month is 0-indexed)
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        return new Date(year, month, day);
      }
    }
    
    // If it's in MM/DD/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0]) - 1;
        const day = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        return new Date(year, month, day);
      }
    }
    
    // Fallback to regular Date parsing
    return new Date(dateStr);
  }
 
  /**
   * Check if any filters are currently active
   */
  hasActiveFilters(): boolean {
    return this.selectedFilter !== 'all' || 
           this.selectedDagName !== '' || 
           this.selectedDateFilter !== 'all' ;
          // (this.searchTerm && this.searchTerm.trim() !== '');
  }
 
  /**
   * Get filtered data based on current filter and search criteria
   */
  getFilteredData(): any[] {
    let filtered = [...this.dagAnalyticsData];

    // Apply status filter
    if (this.selectedFilter !== 'all') {
      filtered = filtered.filter(dag => {
        if (this.selectedFilter === 'normal') {
          return dag.deviationStatus === 'Normal';
        } else if (this.selectedFilter === 'deviation') {
          return dag.deviationStatus === 'Deviation';
        }
        return true;
      });
    }

    // Apply DAG name filter
    if (this.selectedDagName && this.selectedDagName.trim() !== '') {
      filtered = filtered.filter(dag => 
        dag.dagName === this.selectedDagName
      );
    }
 
    // Apply date filter
    if (this.selectedDateFilter !== 'all') {
      filtered = filtered.filter(dag => {
        const dagDate = new Date(dag.loadDate);
        const dateString = dagDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        return dateString === this.selectedDateFilter;
      });
    }
 
    // // Apply search filter (this will work in addition to DAG name dropdown)
    // if (this.searchTerm && this.searchTerm.trim() !== '') {
    //   const searchLower = this.searchTerm.toLowerCase().trim();
    //   filtered = filtered.filter(dag => 
    //     dag.dagName.toLowerCase().includes(searchLower)
    //   );
    // }

    return filtered;
  }

  /**
   * Update filtered data (for internal use)
   */
  private updateFilteredData(): void {
    this.filteredData = this.getFilteredData();
  }

  /**
   * TrackBy function for ngFor performance optimization
   */
  trackByDagName(index: number, dag: any): string {
    return dag.dagName + dag.loadDate;
  }

    /**
   * TrackBy function for date options
   */
  trackByDateValue(index: number, dateOption: any): string {
    return dateOption.value;
  }

  /**
   * Get progress bar width percentage
   */
  getProgressWidth(dayCount: number, avgDayCount: number): number {
    if (avgDayCount === 0) return 0;
    const maxValue = Math.max(dayCount, avgDayCount);
    return Math.min((dayCount / maxValue) * 100, 100);
  }

  /**
   * Get maximum value for progress bar scaling
   */
  getMaxValue(dayCount: number, avgDayCount: number): number {
    return Math.max(dayCount, avgDayCount);
  }

  /**
    * View details for a specific DAG - Opens detailed modal
   */
  viewDagDetails(dag: any): void {
    this.selectedDagForDetails = dag;
  }
 
  /**
   * Close DAG details modal
   */
  closeDagDetails(): void {
    this.selectedDagForDetails = null;
  }
 
  /**
   * Format date with more detail for modal
   */
  formatDateDetailed(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
 
  /**
   * Get performance trend description
   */
  getPerformanceTrend(dag: any): string {
    const diff = parseFloat(dag.percentageDifference);
    if (diff > 50) return 'Significantly Higher';
    if (diff > 25) return 'Notably Higher';
    if (diff > 15) return 'Moderately Higher';
    if (diff > 0) return 'Slightly Higher';
    if (diff === 0) return 'Exactly Average';
    if (diff > -15) return 'Slightly Lower';
    if (diff > -25) return 'Moderately Lower';
    if (diff > -50) return 'Notably Lower';
    return 'Significantly Lower';
  }
 
  /**
   * Get deviation severity level
   */
  getDeviationSeverity(dag: any): string {
    const diff = parseFloat(dag.percentageDifference);
    if (diff > 50 || diff < -50) return 'Critical';
    if (diff > 25 || diff < -25) return 'High';
    if (diff > 15 || diff < -15) return 'Medium';
    if (diff > 0 || diff < 0) return 'Low';
    return 'Normal';
  }
 
  /**
   * Get CSS class for deviation severity
   */
  getDeviationSeverityClass(dag: any): string {
    const severity = this.getDeviationSeverity(dag);
    return severity.toLowerCase();
  }
 
  /**
   * Get recommendation based on performance
   */
  getRecommendation(dag: any): string {
    const diff = parseFloat(dag.percentageDifference);
    const status = dag.deviationStatus;
    if (status === 'Normal') {
      return 'DAG performance is within normal parameters. Continue monitoring.';
    }
    if (diff > 0) {
      if (diff > 50) {
        return 'DAG is running significantly more than average. Investigate potential issues or increased workload.';
      } else if (diff > 25) {
        return 'DAG execution count is notably high. Monitor for potential resource constraints.';
      } else {
        return 'DAG is running more frequently than average. This may indicate increased demand or processing load.';
      }
    } else {
      if (diff < -50) {
        return 'DAG execution is critically low. Check for failures, scheduling issues, or data pipeline problems.';
      } else if (diff < -25) {
        return 'DAG execution count is notably low. Investigate potential issues or reduced data flow.';
      } else {
        return 'DAG is running less frequently than average. Monitor for any potential issues.';
      }
    }
  }
 
  /**
   * Get average marker position for detailed progress bar
   */
  getAverageMarkerPosition(dayCount: number, avgDayCount: number): number {
    if (avgDayCount === 0) return 0;
    const maxValue = Math.max(dayCount, avgDayCount);
    return Math.min((avgDayCount / maxValue) * 100, 100);
  }

  /**
   * Get unique DAG names for dropdown (sorted alphabetically)
   */
  getUniqueDagNames(): string[] {
    const uniqueNames = [...new Set(this.dagAnalyticsData.map(dag => dag.dagName))];
    return uniqueNames.sort();
  }
 
  /**
   * Get unique dates for dropdown with formatted labels
   * Always includes today and yesterday even if no data exists
   */
  getUniqueDates(): { value: string, label: string }[] {
    // Get dates from actual data
    const dataUniqueDates = [...new Set(this.dagAnalyticsData.map(dag => {
      const date = new Date(dag.loadDate);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    }))];
    
    // Always include today and yesterday using local timezone
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // Combine and deduplicate
    const allUniqueDates = [...new Set([todayStr, yesterdayStr, ...dataUniqueDates])];
    
    return allUniqueDates
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime()) // Sort newest first
      .map(dateStr => {
        // Parse date string properly to avoid timezone issues
        const dateParts = dateStr.split('-');
        const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        
        // Format as readable label
        let label = date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        });
        
        // Add special labels for recent dates
        if (dateStr === todayStr) {
          label = `Today (${label})`;
        } else if (dateStr === yesterdayStr) {
          label = `Yesterday (${label})`;
        }
        
        return { value: dateStr, label: label };
      });
  }
 
  /**
   * Handle DAG name filter change
   */
  onDagNameFilterChange(): void {
    this.updateFilteredData();
  }
 
  /**
   * Handle date filter change
   */
    onDateFilterChange(): void {
    this.updateFilteredData();
  }

  /**
   * Export data for a single DAG
   */
  exportSingleDag(dag: any): void {
    const singleDagData = [dag];
    const exportData = singleDagData.map(item => {
      const processedItem = {
        ...item,
        dayOfWeekName: this.getDayOfWeekName(item.dayOfWeek),
        percentageDifference: this.getPercentageDifference(item.dayCount, item.avgDayCount) + '%',
        deviationStatus: this.isSignificantDifference(item.dayCount, item.avgDayCount) ? 'Deviation' : 'Normal',
        loadDate: this.formatDate(new Date(item.loadDate))
      };
      return processedItem;
    });
    // Create data rows using the column order and mapping
    this.dataForExcel = [];
    exportData.forEach((row: any) => {
      const rowData = this.ordersTableExcelHeaderColumns.map(col => row[col.dataKey]);
      this.dataForExcel.push(rowData);
    });
    // Use the proper column names from ordersTableExcelHeaderColumns
    const columnHeaders = this.ordersTableExcelHeaderColumns.map(col => col.name);
    const reportData = {
      title: `DAG_${dag.dagName}_Analytics_Report`,
      data: this.dataForExcel,
      headers: columnHeaders
    };
    this.ete.exportExcel(reportData);
  }
}